import {
  ChevronLeft,
  ChevronRight,
  FilterX,
  Inbox,
  RefreshCw,
  Search,
  TriangleAlert,
  User,
  UserRound,
  Users,
  UserX,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type {
  AdminMeetingListItem,
  AdminMeetingListResponse,
  AdminMeetingSort,
  MeetingAttendance,
  MeetingStatus,
} from '@hiredude/meet-shared';

import {
  errorMessage,
  isSessionExpired,
  REQUEST_TIMEOUT_MS,
  withTimeout,
} from '@/components/admin/adminError';
import { endOfDayIso, startOfDayIso } from '@/components/admin/datetime';
import { formatCount, formatDateTime, formatDurationMs, formatMinutes } from '@/components/admin/format';
import { JoinWindowSummary } from '@/components/admin/joinWindow';
import {
  MEETING_STATUSES,
  MEETING_STATUS_META,
  MeetingStatusBadge,
  RecordingStatusBadge,
  StatusDot,
  isMeetingStatus,
} from '@/components/admin/status';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { adminMeetings } from '@/lib/adminApi';
import type { MeetingListParams } from '@/lib/adminApi';
import { cn } from '@/lib/utils';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const PAGE_SIZES = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

function readPage(raw: string | null): number {
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}

function readPageSize(raw: string | null): number {
  const parsed = Number(raw);
  return (PAGE_SIZES as readonly number[]).includes(parsed) ? parsed : DEFAULT_PAGE_SIZE;
}

/**
 * The meetings table.
 *
 * Every filter lives in the query string rather than in component state, so a
 * view survives a reload, can be linked to from the dashboard, and can be
 * pasted to a colleague.
 */
export function AdminMeetingsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statuses = useMemo(
    () => searchParams.getAll('status').filter(isMeetingStatus),
    [searchParams],
  );
  const query = searchParams.get('q')?.trim() ?? '';
  const fromDay = DAY.test(searchParams.get('from') ?? '') ? (searchParams.get('from') ?? '') : '';
  const toDay = DAY.test(searchParams.get('to') ?? '') ? (searchParams.get('to') ?? '') : '';
  const page = readPage(searchParams.get('page'));
  const pageSize = readPageSize(searchParams.get('pageSize'));
  const sort: AdminMeetingSort = searchParams.get('sort') === 'createdAt' ? 'createdAt' : 'startsAt';
  const direction = searchParams.get('direction') === 'asc' ? 'asc' : 'desc';

  const params = useMemo<MeetingListParams>(
    () => ({
      status: statuses,
      q: query || undefined,
      from: fromDay ? (startOfDayIso(fromDay) ?? undefined) : undefined,
      to: toDay ? (endOfDayIso(toDay) ?? undefined) : undefined,
      page,
      pageSize,
      sort,
      direction,
    }),
    [statuses, query, fromDay, toDay, page, pageSize, sort, direction],
  );

  const [data, setData] = useState<AdminMeetingListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fetching, setFetching] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  const commit = useCallback(
    (
      mutate: (next: URLSearchParams) => void,
      options?: { keepPage?: boolean; replace?: boolean },
    ) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          mutate(next);
          // Any change to the filters invalidates the page number: page 7 of a
          // narrower result set is usually empty.
          if (!options?.keepPage) next.delete('page');
          return next;
        },
        { replace: options?.replace ?? false },
      );
    },
    [setSearchParams],
  );

  const [searchInput, setSearchInput] = useState(query);

  // Adopt the URL's value when it changes from elsewhere (a dashboard link, the
  // Clear filters button, the back button) without stamping on what is being
  // typed right now.
  useEffect(() => {
    setSearchInput((current) => (current.trim() === query ? current : query));
  }, [query]);

  useEffect(() => {
    const next = searchInput.trim();
    if (next === query) return;

    const timer = window.setTimeout(() => {
      commit(
        (target) => {
          if (next) target.set('q', next);
          else target.delete('q');
        },
        // Replace, or every keystroke would become a back-button stop.
        { replace: true },
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput, query, commit]);

  useEffect(() => {
    let ignore = false;
    setFetching(true);

    withTimeout(adminMeetings(params), REQUEST_TIMEOUT_MS, 'The server took too long to respond.')
      .then((result) => {
        // A superseded request lands here after its cleanup ran, so `ignore`
        // also protects against a slow page 1 overwriting a fast page 2.
        if (ignore) return;
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (isSessionExpired(err)) {
          navigate('/admin/login', { replace: true });
          return;
        }
        setError(errorMessage(err, 'Could not load meetings.'));
      })
      .finally(() => {
        if (!ignore) setFetching(false);
      });

    return () => {
      ignore = true;
    };
  }, [params, reloadToken, navigate]);

  const toggleStatus = useCallback(
    (status: MeetingStatus) => {
      commit((target) => {
        const current = target.getAll('status').filter(isMeetingStatus);
        const next = current.includes(status)
          ? current.filter((value) => value !== status)
          : [...current, status];

        target.delete('status');
        for (const value of next) target.append('status', value);
      });
    },
    [commit],
  );

  const clearFilters = useCallback(() => {
    commit((target) => {
      target.delete('status');
      target.delete('q');
      target.delete('from');
      target.delete('to');
    });
  }, [commit]);

  const goToPage = useCallback(
    (next: number) => {
      commit(
        (target) => {
          if (next <= 1) target.delete('page');
          else target.set('page', String(next));
        },
        { keepPage: true },
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [commit],
  );

  const hasFilters = statuses.length > 0 || query !== '' || fromDay !== '' || toDay !== '';
  const datesReversed = fromDay !== '' && toDay !== '' && fromDay > toDay;
  const pageCount = data ? Math.max(1, data.pageCount) : 1;

  return (
    <div className="mx-auto w-full max-w-7xl p-4 sm:p-6 lg:p-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Meetings</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {data ? `${formatCount(data.total)} matching this view` : 'Loading…'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setReloadToken((token) => token + 1)}
          disabled={fetching}
        >
          {fetching && data ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
          Refresh
        </Button>
      </header>

      <Card className="mt-6 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Label htmlFor="meeting-search" className="sr-only">
              Search meetings by title, code or participant
            </Label>
            <Search
              aria-hidden
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 z-10 size-4 -translate-y-1/2"
            />
            <Input
              id="meeting-search"
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search title, code or name"
              autoComplete="off"
              spellCheck={false}
              className="pr-10 pl-10 text-sm"
            />
            {searchInput !== '' && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchInput('')}
                className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1.5 grid size-8 -translate-y-1/2 place-items-center rounded-md transition-colors"
              >
                <X className="size-4" />
              </button>
            )}
          </div>

          {/* Stacked on a phone: side by side, the longest option label does not
              fit in half of a 360px screen. */}
          <div className="grid grid-cols-1 gap-2 sm:flex sm:items-center">
            <div className="min-w-0">
              <Label htmlFor="meeting-sort" className="sr-only">
                Sort meetings by
              </Label>
              <Select
                id="meeting-sort"
                value={sort}
                wrapperClassName="sm:w-44"
                onChange={(event) =>
                  commit((target) => {
                    if (event.target.value === 'createdAt') target.set('sort', 'createdAt');
                    else target.delete('sort');
                  })
                }
              >
                <option value="startsAt">Scheduled time</option>
                <option value="createdAt">Created time</option>
              </Select>
            </div>

            <div className="min-w-0">
              <Label htmlFor="meeting-direction" className="sr-only">
                Sort order
              </Label>
              <Select
                id="meeting-direction"
                value={direction}
                wrapperClassName="sm:w-36"
                onChange={(event) =>
                  commit((target) => {
                    if (event.target.value === 'asc') target.set('direction', 'asc');
                    else target.delete('direction');
                  })
                }
              >
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </Select>
            </div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:max-w-md">
          <div>
            <Label
              htmlFor="meeting-from"
              className="text-muted-foreground mb-1.5 block text-[11px] font-semibold tracking-[0.09em] uppercase"
            >
              From
            </Label>
            <Input
              id="meeting-from"
              type="date"
              value={fromDay}
              max={toDay || undefined}
              onChange={(event) =>
                commit((target) => {
                  if (event.target.value) target.set('from', event.target.value);
                  else target.delete('from');
                })
              }
            />
          </div>
          <div>
            <Label
              htmlFor="meeting-to"
              className="text-muted-foreground mb-1.5 block text-[11px] font-semibold tracking-[0.09em] uppercase"
            >
              To
            </Label>
            <Input
              id="meeting-to"
              type="date"
              value={toDay}
              min={fromDay || undefined}
              onChange={(event) =>
                commit((target) => {
                  if (event.target.value) target.set('to', event.target.value);
                  else target.delete('to');
                })
              }
            />
          </div>
        </div>
        {datesReversed && (
          <p className="mt-2 text-xs text-[var(--warning)]">From is after To, so this view is empty.</p>
        )}

        <Separator className="my-4" />

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground mr-1 text-[11px] font-semibold tracking-[0.09em] uppercase">
            Status
          </span>
          {MEETING_STATUSES.map((status) => {
            const meta = MEETING_STATUS_META[status];
            const active = statuses.includes(status);

            return (
              <button
                key={status}
                type="button"
                aria-pressed={active}
                onClick={() => toggleStatus(status)}
                className={cn(
                  'inline-flex min-h-11 items-center gap-2 rounded-full border px-3.5 text-sm font-medium transition-colors',
                  active
                    ? 'border-primary/50 bg-primary/15 text-foreground'
                    : 'border-border text-muted-foreground hover:text-foreground hover:bg-white/[0.06]',
                )}
              >
                <StatusDot variant={meta.variant} className="size-1.5" />
                {meta.label}
              </button>
            );
          })}

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto">
              <FilterX className="size-4" />
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      {data && (
        <AttendanceStrip attendance={data.attendance} total={data.total} dimmed={fetching} />
      )}

      <div className="mt-4">
        {!data && fetching && <MeetingsSkeleton />}

        {!data && !fetching && error && (
          <Card className="border-[color-mix(in_oklab,var(--destructive)_40%,transparent)] p-6">
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-[color-mix(in_oklab,var(--destructive)_16%,transparent)] text-[color-mix(in_oklab,var(--destructive)_80%,white)]">
                <TriangleAlert className="size-5" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold tracking-tight">Could not load meetings</h2>
                <p className="text-muted-foreground mt-1 text-sm text-pretty" role="alert">
                  {error}
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => setReloadToken((token) => token + 1)}
              >
                <RefreshCw className="size-4" />
                Retry
              </Button>
            </div>
          </Card>
        )}

        {data && (
          <>
            {error && (
              <p
                role="alert"
                className="mb-4 rounded-xl border border-[color-mix(in_oklab,var(--warning)_35%,transparent)] bg-[color-mix(in_oklab,var(--warning)_12%,transparent)] px-3.5 py-2.5 text-sm text-[var(--warning)]"
              >
                {error} Showing the rows we already had.
              </p>
            )}

            {data.meetings.length === 0 ? (
              <EmptyState
                hasFilters={hasFilters}
                total={data.total}
                onClearFilters={clearFilters}
                onFirstPage={() => goToPage(1)}
              />
            ) : (
              <div
                aria-busy={fetching}
                className={cn('transition-opacity duration-150', fetching && 'opacity-50')}
              >
                {/* Two renderings of the same rows: a real table once there is
                    room for five columns, and stacked cards before that. A
                    horizontally scrolling table is unusable on a phone. */}
                <Card className="hidden overflow-hidden p-0 md:block">
                  <Table className="table-fixed">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead className="w-36">When</TableHead>
                        <TableHead className="w-44">Status</TableHead>
                        <TableHead className="w-28">Participants</TableHead>
                        <TableHead className="w-40">Together</TableHead>
                        <TableHead className="hidden w-36 lg:table-cell">Recording</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.meetings.map((meeting) => (
                        <MeetingTableRow key={meeting.meetingId} meeting={meeting} />
                      ))}
                    </TableBody>
                  </Table>
                </Card>

                <ul className="space-y-3 md:hidden">
                  {data.meetings.map((meeting) => (
                    <MeetingCard key={meeting.meetingId} meeting={meeting} />
                  ))}
                </ul>
              </div>
            )}

            <PaginationFooter
              page={data.page}
              pageCount={pageCount}
              pageSize={data.pageSize}
              total={data.total}
              rows={data.meetings.length}
              onPage={goToPage}
              onPageSize={(next) =>
                commit((target) => {
                  if (next === DEFAULT_PAGE_SIZE) target.delete('pageSize');
                  else target.set('pageSize', String(next));
                })
              }
            />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * `bothJoined` false on an ended meeting is the quiet failure worth surfacing:
 * the slot came and went but the two people were never in the room together.
 */
const ATTENDANCE: {
  key: keyof MeetingAttendance;
  label: string;
  hint: string;
  icon: LucideIcon;
  tone: 'success' | 'warning' | 'neutral';
}[] = [
  {
    key: 'bothJoined',
    label: 'Both joined',
    hint: 'Recruiter and candidate each connected',
    icon: Users,
    tone: 'success',
  },
  {
    key: 'onlyCandidate',
    label: 'Only candidate',
    hint: 'The recruiter never connected',
    icon: User,
    tone: 'warning',
  },
  {
    key: 'onlyRecruiter',
    label: 'Only recruiter',
    hint: 'The candidate never connected',
    icon: UserRound,
    tone: 'warning',
  },
  {
    key: 'nobodyJoined',
    label: 'Nobody joined',
    hint: 'Neither side connected',
    icon: UserX,
    tone: 'neutral',
  },
];

const ATTENDANCE_RING = {
  success: 'border-[color-mix(in_oklab,var(--success)_30%,transparent)]',
  warning: 'border-[color-mix(in_oklab,var(--warning)_35%,transparent)]',
  neutral: 'border-border/70',
} as const;

const ATTENDANCE_ICON = {
  success: 'bg-[color-mix(in_oklab,var(--success)_14%,transparent)] text-[var(--success)]',
  warning: 'bg-[color-mix(in_oklab,var(--warning)_14%,transparent)] text-[var(--warning)]',
  neutral: 'bg-white/[0.06] text-muted-foreground',
} as const;

const ATTENDANCE_VALUE = {
  success: 'text-[var(--success)]',
  warning: 'text-[var(--warning)]',
  neutral: 'text-foreground',
} as const;

function AttendanceStrip({
  attendance,
  total,
  dimmed,
}: {
  attendance: MeetingAttendance;
  total: number;
  dimmed: boolean;
}) {
  return (
    <section aria-label="Attendance" className={cn('mt-4', dimmed && 'opacity-60')}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium tracking-tight">Attendance</h2>
        <p className="text-muted-foreground text-xs text-pretty">
          Of {formatCount(total)} meetings in this view. A meeting that has not started yet counts
          as nobody joined.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {ATTENDANCE.map((item) => {
          const Icon = item.icon;
          return (
            <Card key={item.key} className={cn('flex flex-col gap-3 p-4', ATTENDANCE_RING[item.tone])}>
              <div className="flex items-start justify-between gap-3">
                <p className="text-muted-foreground text-[11px] font-semibold tracking-[0.09em] uppercase">
                  {item.label}
                </p>
                <span
                  className={cn(
                    'grid size-8 shrink-0 place-items-center rounded-lg',
                    ATTENDANCE_ICON[item.tone],
                  )}
                >
                  <Icon className="size-4" aria-hidden />
                </span>
              </div>
              <p
                className={cn(
                  'text-2xl leading-none font-semibold tracking-tight tabular-nums',
                  ATTENDANCE_VALUE[item.tone],
                )}
              >
                {formatCount(attendance[item.key])}
              </p>
              <p className="text-muted-foreground text-xs text-pretty">{item.hint}</p>
            </Card>
          );
        })}
      </div>
    </section>
  );
}

function OverlapLabel({ overlapMs }: { overlapMs: number | null }) {
  if (overlapMs === null) {
    return (
      <span
        className="text-muted-foreground text-xs text-pretty"
        title="A join or leave is missing, so the time they spent together is not known."
      >
        Cannot be calculated
      </span>
    );
  }
  return <span className="text-sm tabular-nums">{formatDurationMs(overlapMs)}</span>;
}

function neverConnected(meeting: AdminMeetingListItem): boolean {
  return meeting.status === 'ended' && !meeting.bothJoined;
}

function MeetingTableRow({ meeting }: { meeting: AdminMeetingListItem }) {
  return (
    <TableRow className="relative transition-colors hover:bg-white/[0.04]">
      <TableCell className="max-w-0">
        {/* The stretched pseudo-element makes the whole row clickable while
            keeping valid table markup and a single tab stop. */}
        <Link
          to={`/admin/meetings/${meeting.meetingId}`}
          className="block truncate rounded-md text-sm font-medium after:absolute after:inset-0 after:content-['']"
          title={meeting.title}
        >
          {meeting.title}
        </Link>
        <span className="text-muted-foreground mt-0.5 block truncate font-mono text-xs">
          {meeting.code}
        </span>
      </TableCell>

      <TableCell>
        <span className="block text-sm">{formatDateTime(meeting.startsAt)}</span>
        <span className="text-muted-foreground mt-0.5 block text-xs">
          {formatMinutes(meeting.durationMin)}
        </span>
      </TableCell>

      <TableCell>
        <MeetingStatusBadge status={meeting.status} />
        <JoinWindowSummary input={meeting} compact showBadge={false} className="mt-1.5" />
      </TableCell>

      <TableCell>
        <span className="block text-sm tabular-nums">{meeting.participantsJoined} joined</span>
        {neverConnected(meeting) && (
          <span
            className="mt-0.5 flex items-center gap-1 text-xs text-[var(--warning)]"
            title="The two parties were never in the room together, so the interview did not happen."
          >
            <UserX className="size-3 shrink-0" aria-hidden />
            never connected
          </span>
        )}
      </TableCell>

      <TableCell>
        <OverlapLabel overlapMs={meeting.overlapMs} />
      </TableCell>

      <TableCell className="hidden lg:table-cell">
        <RecordingStatusBadge state={meeting.recordingState} />
      </TableCell>
    </TableRow>
  );
}

function MeetingCard({ meeting }: { meeting: AdminMeetingListItem }) {
  return (
    <li className="relative">
      <Card className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <Link
              to={`/admin/meetings/${meeting.meetingId}`}
              className="block rounded-md text-sm font-medium after:absolute after:inset-0 after:content-['']"
            >
              <span className="line-clamp-2">{meeting.title}</span>
            </Link>
            <p className="text-muted-foreground mt-1 truncate font-mono text-xs">{meeting.code}</p>
          </div>
          <MeetingStatusBadge status={meeting.status} className="shrink-0" />
        </div>

        <JoinWindowSummary input={meeting} compact className="mt-3" />

        <Separator className="my-3" />

        <dl className="grid grid-cols-2 gap-x-3 gap-y-3 text-xs">
          <div className="min-w-0">
            <dt className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
              When
            </dt>
            <dd className="mt-1">
              {formatDateTime(meeting.startsAt)}
              <span className="text-muted-foreground block">
                {formatMinutes(meeting.durationMin)}
              </span>
            </dd>
          </div>

          <div className="min-w-0">
            <dt className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
              Participants
            </dt>
            <dd className="mt-1 tabular-nums">
              {meeting.participantsJoined} joined
              {neverConnected(meeting) && (
                <span className="mt-0.5 flex items-center gap-1 text-[var(--warning)]">
                  <UserX className="size-3 shrink-0" aria-hidden />
                  never connected
                </span>
              )}
            </dd>
          </div>

          <div className="col-span-2 min-w-0">
            <dt className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
              Together
            </dt>
            <dd className="mt-1">
              <OverlapLabel overlapMs={meeting.overlapMs} />
            </dd>
          </div>

          <div className="col-span-2 min-w-0">
            <dt className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
              Recording
            </dt>
            <dd className="mt-1.5">
              <RecordingStatusBadge state={meeting.recordingState} />
            </dd>
          </div>
        </dl>
      </Card>
    </li>
  );
}

function PaginationFooter({
  page,
  pageCount,
  pageSize,
  total,
  rows,
  onPage,
  onPageSize,
}: {
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  rows: number;
  onPage: (next: number) => void;
  onPageSize: (next: number) => void;
}) {
  const from = rows === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = rows === 0 ? 0 : from + rows - 1;

  return (
    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-muted-foreground text-sm tabular-nums">
          Showing {formatCount(from)}–{formatCount(to)} of {formatCount(total)}
        </p>
        <div className="flex items-center gap-2">
          <Label
            htmlFor="meeting-page-size"
            className="text-muted-foreground text-xs whitespace-nowrap"
          >
            Per page
          </Label>
          <Select
            id="meeting-page-size"
            value={String(pageSize)}
            wrapperClassName="w-24"
            className="min-h-9"
            onChange={(event) => onPageSize(Number(event.target.value))}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 sm:justify-end">
        <Button
          variant="outline"
          size="sm"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft className="size-4" />
          <span className="max-sm:sr-only">Previous</span>
        </Button>

        <span className="text-muted-foreground text-sm tabular-nums" aria-live="polite">
          Page {formatCount(page)} of {formatCount(pageCount)}
        </span>

        <Button
          variant="outline"
          size="sm"
          aria-label="Next page"
          disabled={page >= pageCount}
          onClick={() => onPage(page + 1)}
        >
          <span className="max-sm:sr-only">Next</span>
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

function EmptyState({
  hasFilters,
  total,
  onClearFilters,
  onFirstPage,
}: {
  hasFilters: boolean;
  total: number;
  onClearFilters: () => void;
  onFirstPage: () => void;
}) {
  // A non-zero total with no rows means the page number outran the result set,
  // which reads very differently from "nothing matched".
  if (total > 0) {
    return (
      <Card className="p-10 text-center">
        <Inbox className="text-muted-foreground mx-auto size-8" aria-hidden />
        <h2 className="mt-4 font-semibold tracking-tight">This page is empty</h2>
        <p className="text-muted-foreground mx-auto mt-1.5 max-w-sm text-sm text-pretty">
          There are {formatCount(total)} matching meetings, but none on this page.
        </p>
        <Button variant="outline" className="mt-5" onClick={onFirstPage}>
          Go to first page
        </Button>
      </Card>
    );
  }

  if (hasFilters) {
    return (
      <Card className="p-10 text-center">
        <Search className="text-muted-foreground mx-auto size-8" aria-hidden />
        <h2 className="mt-4 font-semibold tracking-tight">No meetings match your filters</h2>
        <p className="text-muted-foreground mx-auto mt-1.5 max-w-sm text-sm text-pretty">
          Try a different search term, or widen the status filter.
        </p>
        <Button variant="outline" className="mt-5" onClick={onClearFilters}>
          <FilterX className="size-4" />
          Clear filters
        </Button>
      </Card>
    );
  }

  return (
    <Card className="p-10 text-center">
      <Inbox className="text-muted-foreground mx-auto size-8" aria-hidden />
      <h2 className="mt-4 font-semibold tracking-tight">No meetings yet</h2>
      <p className="text-muted-foreground mx-auto mt-1.5 max-w-sm text-sm text-pretty">
        Interviews appear here as soon as Hiredude schedules them.
      </p>
    </Card>
  );
}

function MeetingsSkeleton() {
  const rows = [0, 1, 2, 3, 4, 5, 6, 7];

  return (
    <div aria-busy="true" aria-label="Loading meetings">
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((item) => (
          <Card key={item} className="space-y-3 p-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-7 w-12" />
            <Skeleton className="h-3 w-full" />
          </Card>
        ))}
      </div>
      <Card className="hidden overflow-hidden p-0 md:block">
        <div className="border-border/60 border-b px-3 py-3">
          <Skeleton className="h-3 w-40" />
        </div>
        {rows.map((row) => (
          <div key={row} className="border-border/60 flex items-center gap-4 border-b px-3 py-4">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-2/3" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-3.5 w-20" />
          </div>
        ))}
      </Card>

      <ul className="space-y-3 md:hidden">
        {rows.slice(0, 5).map((row) => (
          <li key={row}>
            <Card className="space-y-3 p-4">
              <div className="flex items-start justify-between gap-3">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-3 w-full" />
            </Card>
          </li>
        ))}
      </ul>
    </div>
  );
}
